import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle, FileText, AlertCircle } from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const provinces = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick",
  "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia",
  "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Yukon"
];

const maritalStatuses = ["Single", "Married", "Common-law", "Separated", "Divorced", "Widowed"];

const incomeTypes = [
  { value: "employment", label: "Employment Income (T4)", desc: "I worked as an employee" },
  { value: "self-employment", label: "Self-Employment/Freelance (T4A)", desc: "I have contract or freelance income" },
  { value: "investment", label: "Investment Income (T5/T3)", desc: "I have dividends, interest, or capital gains" },
  { value: "rental", label: "Rental Income", desc: "I rent out property" },
  { value: "ei-social", label: "EI/Social Assistance", desc: "I received Employment Insurance or government benefits" },
  { value: "pension", label: "Pension Income", desc: "I receive pension payments" },
  { value: "covid-benefits", label: "CERB/CRB/Other COVID benefits", desc: "I received pandemic-related benefits" },
  { value: "scholarship", label: "Scholarship/Bursary", desc: "I received educational funding" },
  { value: "foreign", label: "Foreign Income", desc: "I have income from outside Canada" },
  { value: "other", label: "Other", desc: "I have other types of income" },
];

const specialSituations = [
  { value: "moved-provinces", label: "I moved provinces/territories this year" },
  { value: "sold-investments", label: "I sold stocks or crypto and have capital gains/losses" },
  { value: "signing-bonus", label: "I received a signing bonus or stock options" },
  { value: "medical-expenses", label: "I have medical expenses over $3,000" },
  { value: "charitable", label: "I made charitable donations" },
  { value: "childcare", label: "I paid childcare expenses" },
  { value: "student-loan", label: "I have student loan interest to claim" },
  { value: "home-office", label: "I worked from home and can claim home office expenses" },
  { value: "disability", label: "I have a disability (eligible for Disability Tax Credit)" },
  { value: "back-taxes", label: "I need to file taxes for previous years (back taxes)" },
  { value: "none", label: "None of the above" },
];

const incomeRanges = [
  "Under $20,000", "$20,000 - $40,000", "$40,000 - $60,000",
  "$60,000 - $80,000", "$80,000 - $100,000", "$100,000 - $150,000", "Over $150,000"
];

const TaxFilingModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  
  const [formData, setFormData] = useState({
    // Personal Info
    fullName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    // Residency
    province: "",
    maritalStatus: "",
    hasDependents: "",
    numDependents: "",
    // Income
    incomeTypes: [],
    otherIncomeType: "",
    isStudent: "",
    hasTuitionReceipts: "",
    isInternational: "",
    madeRRSP: "",
    hasTFSA: "",
    ownsHome: "",
    // Special situations
    specialSituations: [],
    // Filing details
    taxYear: "",
    multipleYears: [],
    filedBefore: "",
    estimatedIncome: "",
    // Timeline
    urgency: "",
    hasDocuments: "",
    // Additional
    additionalInfo: "",
    // Consents
    taxAuth: false,
    docHandling: false,
    privacySecurity: false,
    pricingAck: false,
  });

  const handleIncomeTypeToggle = (value) => {
    setFormData(prev => {
      const current = prev.incomeTypes;
      if (current.includes(value)) {
        return { ...prev, incomeTypes: current.filter(t => t !== value) };
      } else {
        return { ...prev, incomeTypes: [...current, value] };
      }
    });
  };

  const handleSpecialSituationToggle = (value) => {
    setFormData(prev => {
      const current = prev.specialSituations;
      if (value === "none") {
        return { ...prev, specialSituations: ["none"] };
      }
      const filtered = current.filter(s => s !== "none");
      if (current.includes(value)) {
        return { ...prev, specialSituations: filtered.filter(t => t !== value) };
      } else {
        return { ...prev, specialSituations: [...filtered, value] };
      }
    });
  };

  const handleMultipleYearsToggle = (year) => {
    setFormData(prev => {
      const current = prev.multipleYears;
      if (current.includes(year)) {
        return { ...prev, multipleYears: current.filter(y => y !== year) };
      } else {
        return { ...prev, multipleYears: [...current, year] };
      }
    });
  };

  const validateStep = (stepNum) => {
    switch(stepNum) {
      case 1:
        if (!formData.fullName || formData.fullName.length < 2) {
          toast.error("Please enter your full legal name");
          return false;
        }
        if (!formData.email) {
          toast.error("Please enter your email address");
          return false;
        }
        if (!formData.phone) {
          toast.error("Phone number is required for CPA contact");
          return false;
        }
        if (!formData.dateOfBirth) {
          toast.error("Please enter your date of birth");
          return false;
        }
        if (!formData.province) {
          toast.error("Please select your province");
          return false;
        }
        if (!formData.maritalStatus) {
          toast.error("Please select your marital status");
          return false;
        }
        return true;
      case 2:
        if (formData.incomeTypes.length === 0) {
          toast.error("Please select at least one income type");
          return false;
        }
        if (!formData.isStudent) {
          toast.error("Please indicate if you are a student");
          return false;
        }
        if (!formData.isInternational) {
          toast.error("Please indicate if you are an international student");
          return false;
        }
        return true;
      case 3:
        if (!formData.taxYear) {
          toast.error("Please select the tax year");
          return false;
        }
        if (!formData.filedBefore) {
          toast.error("Please indicate if you have filed before");
          return false;
        }
        if (!formData.estimatedIncome) {
          toast.error("Please select your estimated income range");
          return false;
        }
        if (!formData.urgency) {
          toast.error("Please select your filing urgency");
          return false;
        }
        if (!formData.hasDocuments) {
          toast.error("Please indicate if you have your documents ready");
          return false;
        }
        return true;
      case 4:
        if (!formData.taxAuth || !formData.docHandling || !formData.privacySecurity || !formData.pricingAck) {
          toast.error("Please agree to all terms to proceed");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(s => Math.min(s + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setStep(s => Math.max(s - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(4)) return;

    setLoading(true);
    try {
      // Send to backend
      await axios.post(`${API_URL}/api/premium-service/tax-filing`, {
        service_type: "Professional Tax Filing",
        // Personal Info
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        date_of_birth: formData.dateOfBirth,
        // Residency
        province: formData.province,
        marital_status: formData.maritalStatus,
        has_dependents: formData.hasDependents,
        num_dependents: formData.numDependents || "N/A",
        // Income
        income_types: formData.incomeTypes.map(t => incomeTypes.find(i => i.value === t)?.label).join(", "),
        other_income: formData.otherIncomeType || "N/A",
        is_student: formData.isStudent,
        has_tuition_receipts: formData.hasTuitionReceipts || "N/A",
        is_international: formData.isInternational,
        made_rrsp: formData.madeRRSP,
        has_tfsa: formData.hasTFSA,
        owns_home: formData.ownsHome,
        // Special situations
        special_situations: formData.specialSituations.map(s => specialSituations.find(i => i.value === s)?.label).join(", ") || "None",
        // Filing
        tax_year: formData.taxYear,
        multiple_years: formData.multipleYears.join(", ") || "N/A",
        filed_before: formData.filedBefore,
        estimated_income: formData.estimatedIncome,
        // Timeline
        urgency: formData.urgency,
        has_documents: formData.hasDocuments,
        // Additional
        additional_info: formData.additionalInfo || "None provided",
        // Meta
        price: "$0 (Quote will be provided - Typical: $150-$400 simple, $400-$800 complex)",
        submitted_at: new Date().toISOString(),
      });

      setSuccess(true);
    } catch (error) {
      console.error("Failed to submit tax filing request:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setStep(1);
    setFormData({
      fullName: "", email: "", phone: "", dateOfBirth: "",
      province: "", maritalStatus: "", hasDependents: "", numDependents: "",
      incomeTypes: [], otherIncomeType: "", isStudent: "", hasTuitionReceipts: "",
      isInternational: "", madeRRSP: "", hasTFSA: "", ownsHome: "",
      specialSituations: [], taxYear: "", multipleYears: [], filedBefore: "",
      estimatedIncome: "", urgency: "", hasDocuments: "", additionalInfo: "",
      taxAuth: false, docHandling: false, privacySecurity: false, pricingAck: false,
    });
    onOpenChange(false);
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-heading font-bold text-navy-700 mb-2">
              Request Submitted!
            </h3>
            <p className="text-slate-600">
              A licensed CPA will review your information and contact you within 24-48 hours with a quote and next steps.
            </p>
            <Button onClick={handleClose} className="mt-6 bg-emerald-500 hover:bg-emerald-600">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="font-heading text-xl">
                Get Professional Tax Filing Help
              </DialogTitle>
              <DialogDescription>
                Connect with a licensed Canadian CPA for professional tax preparation.
              </DialogDescription>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  s <= step ? "bg-blue-500" : "bg-slate-200"
                }`}
              />
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-2">Step {step} of {totalSteps}</p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Step 1: Personal Info & Residency */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-navy-700">Personal Information & Residency</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Legal Name *</Label>
                  <Input
                    placeholder="First and Last Name (as appears on ID)"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone Number * <span className="text-xs text-slate-500">(Required for CPA contact)</span></Label>
                  <Input
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth *</Label>
                  <Input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Province/Territory of Residence *</Label>
                  <Select value={formData.province} onValueChange={(v) => setFormData({ ...formData, province: v })}>
                    <SelectTrigger><SelectValue placeholder="Select province" /></SelectTrigger>
                    <SelectContent>
                      {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Marital Status *</Label>
                  <Select value={formData.maritalStatus} onValueChange={(v) => setFormData({ ...formData, maritalStatus: v })}>
                    <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      {maritalStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Do you have dependents (children under 18)? *</Label>
                <RadioGroup value={formData.hasDependents} onValueChange={(v) => setFormData({ ...formData, hasDependents: v })}>
                  <div className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="dep-yes" />
                      <Label htmlFor="dep-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="dep-no" />
                      <Label htmlFor="dep-no">No</Label>
                    </div>
                  </div>
                </RadioGroup>
                {formData.hasDependents === "yes" && (
                  <div className="space-y-2">
                    <Label>How many dependents?</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={formData.numDependents}
                      onChange={(e) => setFormData({ ...formData, numDependents: e.target.value })}
                      className="w-24"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Income & Tax Situation */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-navy-700">Income & Tax Situation</h3>
              
              <div className="space-y-3">
                <Label>What type of income do you have? * (Select all that apply)</Label>
                <div className="grid grid-cols-1 gap-2 max-h-[200px] overflow-y-auto border rounded-lg p-3">
                  {incomeTypes.map((type) => (
                    <div
                      key={type.value}
                      className={`flex items-start space-x-3 p-2 rounded cursor-pointer transition-colors ${
                        formData.incomeTypes.includes(type.value) ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                      onClick={() => handleIncomeTypeToggle(type.value)}
                    >
                      <Checkbox checked={formData.incomeTypes.includes(type.value)} />
                      <div>
                        <span className="text-sm font-medium">{type.label}</span>
                        <span className="text-xs text-slate-500 block">{type.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {formData.incomeTypes.includes("other") && (
                <div className="space-y-2">
                  <Label>Please specify other income type</Label>
                  <Input
                    placeholder="Describe your other income..."
                    value={formData.otherIncomeType}
                    onChange={(e) => setFormData({ ...formData, otherIncomeType: e.target.value })}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Label>Are you a student? *</Label>
                  <RadioGroup value={formData.isStudent} onValueChange={(v) => setFormData({ ...formData, isStudent: v })}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="full-time" id="student-ft" />
                      <Label htmlFor="student-ft">Yes - Full-time</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="part-time" id="student-pt" />
                      <Label htmlFor="student-pt">Yes - Part-time</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="student-no" />
                      <Label htmlFor="student-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-3">
                  <Label>Are you an international student? *</Label>
                  <RadioGroup value={formData.isInternational} onValueChange={(v) => setFormData({ ...formData, isInternational: v })}>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="intl-yes" />
                      <Label htmlFor="intl-yes">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="intl-no" />
                      <Label htmlFor="intl-no">No - Canadian citizen/PR</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {(formData.isStudent === "full-time" || formData.isStudent === "part-time") && (
                <div className="space-y-3">
                  <Label>Do you have tuition receipts (T2202)?</Label>
                  <RadioGroup value={formData.hasTuitionReceipts} onValueChange={(v) => setFormData({ ...formData, hasTuitionReceipts: v })}>
                    <div className="flex gap-4">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="tuition-yes" />
                        <Label htmlFor="tuition-yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="tuition-no" />
                        <Label htmlFor="tuition-no">No</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-3">
                  <Label>Made RRSP contributions?</Label>
                  <RadioGroup value={formData.madeRRSP} onValueChange={(v) => setFormData({ ...formData, madeRRSP: v })}>
                    <div className="flex gap-3">
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="yes" id="rrsp-yes" />
                        <Label htmlFor="rrsp-yes" className="text-sm">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="no" id="rrsp-no" />
                        <Label htmlFor="rrsp-no" className="text-sm">No</Label>
                      </div>
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="unsure" id="rrsp-unsure" />
                        <Label htmlFor="rrsp-unsure" className="text-sm">Not Sure</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
                <div className="space-y-3">
                  <Label>Have a TFSA?</Label>
                  <RadioGroup value={formData.hasTFSA} onValueChange={(v) => setFormData({ ...formData, hasTFSA: v })}>
                    <div className="flex gap-3">
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="yes" id="tfsa-yes" />
                        <Label htmlFor="tfsa-yes" className="text-sm">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="no" id="tfsa-no" />
                        <Label htmlFor="tfsa-no" className="text-sm">No</Label>
                      </div>
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="unsure" id="tfsa-unsure" />
                        <Label htmlFor="tfsa-unsure" className="text-sm">Not Sure</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
                <div className="space-y-3">
                  <Label>Own a home?</Label>
                  <RadioGroup value={formData.ownsHome} onValueChange={(v) => setFormData({ ...formData, ownsHome: v })}>
                    <div className="flex gap-2 flex-wrap">
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="yes" id="home-yes" />
                        <Label htmlFor="home-yes" className="text-xs">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="first-time" id="home-first" />
                        <Label htmlFor="home-first" className="text-xs">First home this year</Label>
                      </div>
                      <div className="flex items-center space-x-1">
                        <RadioGroupItem value="no" id="home-no" />
                        <Label htmlFor="home-no" className="text-xs">Rent</Label>
                      </div>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              <div className="space-y-3">
                <Label>Do any of these apply to you? (Optional)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[180px] overflow-y-auto border rounded-lg p-3">
                  {specialSituations.map((situation) => (
                    <div
                      key={situation.value}
                      className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                        formData.specialSituations.includes(situation.value) ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                      onClick={() => handleSpecialSituationToggle(situation.value)}
                    >
                      <Checkbox checked={formData.specialSituations.includes(situation.value)} />
                      <span className="text-sm">{situation.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Filing Details & Timeline */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-navy-700">Filing Details & Timeline</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Which tax year are you filing for? *</Label>
                  <Select value={formData.taxYear} onValueChange={(v) => setFormData({ ...formData, taxYear: v })}>
                    <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2024">2024 (Current year)</SelectItem>
                      <SelectItem value="2023">2023</SelectItem>
                      <SelectItem value="2022">2022</SelectItem>
                      <SelectItem value="2021">2021</SelectItem>
                      <SelectItem value="multiple">Multiple years (need to catch up)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Estimated annual income *</Label>
                  <Select value={formData.estimatedIncome} onValueChange={(v) => setFormData({ ...formData, estimatedIncome: v })}>
                    <SelectTrigger><SelectValue placeholder="Select range" /></SelectTrigger>
                    <SelectContent>
                      {incomeRanges.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.taxYear === "multiple" && (
                <div className="space-y-2">
                  <Label>Which years do you need to file?</Label>
                  <div className="flex flex-wrap gap-2">
                    {["2024", "2023", "2022", "2021", "2020", "2019"].map(year => (
                      <Button
                        key={year}
                        type="button"
                        variant={formData.multipleYears.includes(year) ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleMultipleYearsToggle(year)}
                      >
                        {year}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Label>Have you ever filed taxes in Canada before? *</Label>
                <RadioGroup value={formData.filedBefore} onValueChange={(v) => setFormData({ ...formData, filedBefore: v })}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes-self" id="filed-self" />
                    <Label htmlFor="filed-self">Yes - I filed last year myself</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes-accountant" id="filed-acct" />
                    <Label htmlFor="filed-acct">Yes - An accountant filed for me</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="filed-no" />
                    <Label htmlFor="filed-no">No - First time filing in Canada</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>When do you need your taxes filed by? *</Label>
                <RadioGroup value={formData.urgency} onValueChange={(v) => setFormData({ ...formData, urgency: v })}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="asap" id="urgency-asap" />
                    <Label htmlFor="urgency-asap" className="flex items-center gap-2">
                      ASAP - Tax deadline approaching
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="2-weeks" id="urgency-2w" />
                    <Label htmlFor="urgency-2w">Within 2 weeks</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="1-month" id="urgency-1m" />
                    <Label htmlFor="urgency-1m">Within 1 month</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no-rush" id="urgency-none" />
                    <Label htmlFor="urgency-none">No rush - Filing early</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-3">
                <Label>Do you have all your tax documents ready? *</Label>
                <RadioGroup value={formData.hasDocuments} onValueChange={(v) => setFormData({ ...formData, hasDocuments: v })}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="docs-yes" />
                    <Label htmlFor="docs-yes">Yes - I have everything</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="mostly" id="docs-mostly" />
                    <Label htmlFor="docs-mostly">Mostly - Missing 1-2 documents</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="docs-no" />
                    <Label htmlFor="docs-no">No - Need help gathering documents</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-2">
                <Label>Anything else we should know? (Optional)</Label>
                <Textarea
                  placeholder="Example: I'm self-employed with expenses to claim, I received income from the US, I need to understand my refund options, etc."
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value.slice(0, 1000) })}
                  maxLength={1000}
                  rows={3}
                />
                <p className="text-xs text-slate-500">{formData.additionalInfo.length}/1000 characters</p>
              </div>
            </div>
          )}

          {/* Step 4: Consent & Agreement */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-navy-700">Consent & Agreement</h3>
              
              <div className="space-y-4 bg-slate-50 p-4 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="taxAuth"
                    checked={formData.taxAuth}
                    onCheckedChange={(c) => setFormData({ ...formData, taxAuth: c })}
                  />
                  <Label htmlFor="taxAuth" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                    <strong>Tax Information Authorization *</strong><br />
                    I authorize the assigned CPA to prepare and file my tax return with the Canada Revenue Agency (CRA). I understand the CPA will contact me to review my return before filing.
                  </Label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="docHandling"
                    checked={formData.docHandling}
                    onCheckedChange={(c) => setFormData({ ...formData, docHandling: c })}
                  />
                  <Label htmlFor="docHandling" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                    <strong>Document Handling *</strong><br />
                    I understand I will need to provide tax documents (T4, T5, receipts, etc.) to the CPA via secure upload or email.
                  </Label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="privacySecurity"
                    checked={formData.privacySecurity}
                    onCheckedChange={(c) => setFormData({ ...formData, privacySecurity: c })}
                  />
                  <Label htmlFor="privacySecurity" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                    <strong>Privacy & Security *</strong><br />
                    I consent to my tax information being shared with a licensed CPA for the purpose of tax preparation. All information is encrypted and handled according to CRA privacy standards.
                  </Label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="pricingAck"
                    checked={formData.pricingAck}
                    onCheckedChange={(c) => setFormData({ ...formData, pricingAck: c })}
                  />
                  <Label htmlFor="pricingAck" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                    <strong>Pricing Acknowledgment *</strong><br />
                    I understand the CPA will provide a quote based on my tax situation complexity before starting work. Typical range: $150-$400 for students/simple returns, $400-$800 for complex situations.
                  </Label>
                </div>
              </div>

              {/* Price Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-blue-800 font-medium">Service Price: <span className="text-xl font-bold">$0</span></p>
                <p className="text-blue-600 text-sm">Quote will be provided based on your tax complexity</p>
                <p className="text-blue-500 text-xs mt-1">Typical: $150-$400 simple returns | $400-$800 complex</p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={prevStep}>
                Back
              </Button>
            ) : (
              <div />
            )}
            
            {step < totalSteps ? (
              <Button type="button" onClick={nextStep} className="bg-blue-500 hover:bg-blue-600">
                Next
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Connect Me with a CPA"
                )}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TaxFilingModal;
